import React, { useState, useEffect, useCallback } from "react";

import {
  Typography,
  Grid,
  TextField,
  Button,
  LinearProgress,
  Box,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { getEncoders } from "../api/encoderApi";

const SORT_OPTIONS = [
  { value: 'fecha_desc', label: 'Más reciente', model: [{ field: 'fecha', sort: 'desc' }] },
  { value: 'pacienteId_asc', label: 'ID', model: [{ field: 'pacienteId', sort: 'asc' }] },
  { value: 'paciente_asc', label: 'Nombre', model: [{ field: 'paciente', sort: 'asc' }] },
];

export default function ListEncoder() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCodigo, setFilterCodigo] = useState("");
  const [sortModel, setSortModel] = useState([{ field: 'fecha', sort: 'desc' }]);
  const navigate = useNavigate();

  const currentSort = SORT_OPTIONS.find(
    o => o.model[0]?.field === sortModel[0]?.field && o.model[0]?.sort === sortModel[0]?.sort
  )?.value ?? 'fecha_desc';

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEncoders();
      const formatted = data.map((item) => {
        const dateObj = new Date(item.createdAt);
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const yyyy = dateObj.getFullYear();
        const hh = String(dateObj.getHours()).padStart(2, "0");
        const min = String(dateObj.getMinutes()).padStart(2, "0");
        const fecha = `${dd}/${mm}/${yyyy} ${hh}:${min}`;
        return {
          id: item.codigo,
          codigoPrueba: item.codigoPrueba,
          velocidad: item.velocidad,
          potencia: item.potencia,
          distancia: item.distancia,
          tiempo5STS: item.tiempo5STS,
          pacienteId: item.pacienteId,
          paciente: item.paciente
            ? `${item.paciente.firstname} ${item.paciente.lastname}`
            : "N/A",
          usuario: item.user
            ? `${item.user.firstname} ${item.user.lastname}`
            : "N/A",
          fechaRaw: item.createdAt,
          fecha,
        };
      });
      setRows(formatted);
    } catch (err) {
      console.error("Error fetching encoders:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const filteredRows = rows.filter(
    (row) => !filterCodigo || row.codigoPrueba.includes(filterCodigo)
  );

  const columns = [
    { field: "pacienteId", headerName: "ID", width: 30 },
    { field: "paciente", headerName: "Paciente", width: 180},
    { field: "usuario", headerName: "Profesional", width: 180},
    {
      field: "fecha", headerName: "Fecha", width: 180,
      sortComparator: (v1, v2, cp1, cp2) =>
        new Date(cp1.api.getRow(cp1.id).fechaRaw) - new Date(cp2.api.getRow(cp2.id).fechaRaw),
    },
    { field: "velocidad", headerName: "Velocidad (m/s)", width: 140 },
    { field: "potencia", headerName: "Potencia (w)", width: 140 },
    { field: "distancia", headerName: "Distancia (cm)", width: 140 },
    { field: "tiempo5STS", headerName: "5STS (s)", width: 120 },
    {
      field: "actions",
      headerName: "Acciones",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() =>
            navigate(`/ver-encoder/${params.row.id}/${params.row.pacienteId}`)
          }
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Grid item xs={12}>
      <Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
          <Typography variant="h6" gutterBottom color="#0D6AEE">
            Informes Encoder
          </Typography>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={8} md={4}>
              <TextField
                label="Filtrar Código de Prueba"
                value={filterCodigo}
                onChange={(e) => setFilterCodigo(e.target.value)}
                size="small"
                fullWidth
              />
            </Grid>
            <Grid item>
              <ToggleButtonGroup
                value={currentSort}
                exclusive
                size="small"
                onChange={(_, val) => {
                  const opt = SORT_OPTIONS.find(o => o.value === val);
                  if (opt) setSortModel(opt.model);
                }}
              >
                {SORT_OPTIONS.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
              </ToggleButtonGroup>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                color="secondary"
                onClick={fetchAll}
                startIcon={<RestartAltIcon />}
              >
                Refrescar
              </Button>
            </Grid>
          </Grid>

          {loading ? (
            <LinearProgress />
          ) : (
            <Box sx={{ height: 600, width: "100%" }}>
              <DataGrid
                rows={filteredRows}
                columns={columns}
                pageSize={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                slots={{ toolbar: GridToolbar }}
                disableSelectionOnClick
                sortModel={sortModel}
                onSortModelChange={setSortModel}
              />
            </Box>
          )}
      </Paper>
    </Grid>
  );
}
